// Legal / policy page content for OrgShop. Edit text here as needed.
export const BUSINESS = {
  name: "OrgShop",
  email: "orgshopin@gmail.com",
  phone: "+91 98844 36893",
  address: "3M, Palm Avenue, Sankarapuram 5th Street, Sithalapakkam, Chennai - 600131, Tamil Nadu, India",
  returnWindowDays: 7,
  lastUpdated: "June 22, 2026",
};

export const POLICIES = {
  privacy: {
    title: "Privacy Policy",
    intro:
      `At ${BUSINESS.name}, we respect your privacy and are committed to protecting the personal information you share with us. This Privacy Policy explains how we collect, use, and safeguard your data when you use our website and services.`,
    sections: [
      {
        heading: "1. Information We Collect",
        body: [
          "Contact details such as your phone number, used for OTP-based login and order updates.",
          "Location details (district, city, pincode) to show relevant products and enable delivery.",
          "Order and transaction information, including items purchased and shipping address.",
          "Vendor information (for sellers), such as business and product details.",
        ],
      },
      {
        heading: "2. How We Use Your Information",
        body: [
          "To create and manage your account and authenticate logins via OTP.",
          "To process orders, payments, and deliveries.",
          "To provide customer support and send order-related notifications.",
          "To improve our products, services, and overall user experience.",
        ],
      },
      {
        heading: "3. Payments",
        body: [
          `All online payments are processed securely through our payment gateway partner, Razorpay. ${BUSINESS.name} does not store your card, UPI, or banking credentials on its servers.`,
        ],
      },
      {
        heading: "4. Sharing of Information",
        body: [
          "We do not sell your personal information. We share data only with trusted partners (such as payment gateways and delivery providers) strictly to fulfil your orders, or when required by law.",
        ],
      },
      {
        heading: "5. Data Security",
        body: [
          "We use reasonable administrative and technical measures to protect your information against unauthorised access, alteration, or disclosure.",
        ],
      },
      {
        heading: "6. Your Rights",
        body: [
          "You may request access to, correction of, or deletion of your personal information by contacting us at the email below.",
        ],
      },
      {
        heading: "7. Contact Us",
        body: [
          `For any privacy-related questions, contact us at ${BUSINESS.email} or call ${BUSINESS.phone}.`,
          `Registered address: ${BUSINESS.address}.`,
        ],
      },
    ],
  },

  terms: {
    title: "Terms & Conditions",
    intro:
      `Welcome to ${BUSINESS.name}. By accessing or using our website and services, you agree to be bound by these Terms & Conditions. Please read them carefully.`,
    sections: [
      {
        heading: "1. Eligibility",
        body: [
          "You must be at least 18 years of age and capable of entering into a legally binding agreement to use our services.",
        ],
      },
      {
        heading: "2. Accounts & Authentication",
        body: [
          "Accounts are created and verified using a one-time password (OTP) sent to your phone number. You are responsible for maintaining the confidentiality of your account and for all activity under it.",
        ],
      },
      {
        heading: "3. Products & Pricing",
        body: [
          "We sell organic and natural products, including wood-pressed (Marachekku) cooking oils, sourced from verified vendors.",
          "Prices, offers, and availability are subject to change without prior notice. We strive to display accurate information but do not warrant that product descriptions are error-free.",
        ],
      },
      {
        heading: "4. Vendors",
        body: [
          "Vendors are responsible for the accuracy of their listings, product quality, and compliance with applicable laws. New vendor accounts are subject to approval before their products go live.",
        ],
      },
      {
        heading: "5. Payments",
        body: [
          `Payments are processed securely via Razorpay. By placing an order, you authorise us to charge the applicable amount through the selected payment method.`,
        ],
      },
      {
        heading: "6. Prohibited Use",
        body: [
          "You agree not to misuse the platform, including attempting unauthorised access, posting unlawful content, or interfering with the website's operation.",
        ],
      },
      {
        heading: "7. Limitation of Liability",
        body: [
          `${BUSINESS.name} shall not be liable for any indirect, incidental, or consequential damages arising from the use of our services, to the maximum extent permitted by law.`,
        ],
      },
      {
        heading: "8. Governing Law",
        body: [
          "These Terms are governed by the laws of India, and any disputes shall be subject to the jurisdiction of the courts in Tamil Nadu, India.",
        ],
      },
      {
        heading: "9. Contact Us",
        body: [
          `For questions about these Terms, contact us at ${BUSINESS.email} or call ${BUSINESS.phone}.`,
          `Registered address: ${BUSINESS.address}.`,
        ],
      },
    ],
  },

  shipping: {
    title: "Shipping Policy",
    intro:
      `This Shipping Policy explains how orders placed on ${BUSINESS.name} are processed and delivered.`,
    sections: [
      {
        heading: "1. Order Processing",
        body: [
          "Orders are typically processed within 1–2 business days after payment confirmation. Vendors prepare and dispatch products directly.",
        ],
      },
      {
        heading: "2. Delivery Timelines",
        body: [
          "Estimated delivery is 3–7 business days, depending on your location and product availability.",
          "Delivery times may vary during peak seasons, holidays, or due to unforeseen circumstances.",
        ],
      },
      {
        heading: "3. Shipping Areas",
        body: [
          `We currently deliver across Tamil Nadu and other serviceable locations in India.`,
        ],
      },
      {
        heading: "4. Shipping Charges",
        body: [
          "Applicable shipping charges (if any) are displayed at checkout before you confirm your order.",
        ],
      },
      {
        heading: "5. Tracking & Delays",
        body: [
          "Once your order is dispatched, you will receive updates regarding its status. If your order is delayed beyond the estimated timeline, please contact us for assistance.",
        ],
      },
      {
        heading: "6. Contact Us",
        body: [
          `For shipping-related queries, contact us at ${BUSINESS.email} or call ${BUSINESS.phone}.`,
          `Registered address: ${BUSINESS.address}.`,
        ],
      },
    ],
  },

  refund: {
    title: "Return & Refund Policy",
    intro:
      `We want you to be satisfied with your purchase from ${BUSINESS.name}. This policy explains when returns and refunds are available.`,
    sections: [
      {
        heading: "1. Return Eligibility",
        body: [
          `You may request a return within ${BUSINESS.returnWindowDays} days of delivery if the product is damaged, defective, expired, or incorrect.`,
          "To be eligible, the item must be unused, unopened, and in its original packaging with proof of purchase.",
        ],
      },
      {
        heading: "2. Non-Returnable Items",
        body: [
          "For health and hygiene reasons, opened or used consumable products (such as cooking oils and food items) cannot be returned unless they were received damaged or defective.",
        ],
      },
      {
        heading: "3. How to Request a Return",
        body: [
          `Email us at ${BUSINESS.email} with your order number and a description (and photos, if applicable) of the issue within the return window.`,
          "Our team will review your request and guide you through the next steps.",
        ],
      },
      {
        heading: "4. Refunds",
        body: [
          "Once your return is received and inspected (or your claim is approved), your refund will be processed to the original payment method.",
          "Refunds are typically completed within 5–7 business days via Razorpay, after which it may take additional time for your bank to reflect the amount.",
        ],
      },
      {
        heading: "5. Cancellations",
        body: [
          "Orders can be cancelled before they are dispatched. Once dispatched, the return policy above applies.",
        ],
      },
      {
        heading: "6. Contact Us",
        body: [
          `For returns and refunds, contact us at ${BUSINESS.email} or call ${BUSINESS.phone}.`,
          `Registered address: ${BUSINESS.address}.`,
        ],
      },
    ],
  },
};
